import type {
  ActionsConfig,
  EndpointConfig,
  EntityFormI18nOverrides,
  LayoutConfig,
  WorkflowConfig,
} from '@components/integrated/entity-form';

/**
 * Creates the standard actions configuration used across admin entity forms.
 *
 * @param entityLabel - The human-readable entity name (e.g. "Tenant", "Error Mapping")
 * @param isEdit - Whether the form is in edit mode
 * @param options - Optional overrides (e.g. alignment)
 */
export function createFormActions(
  entityLabel: string,
  isEdit: boolean,
  options?: { alignment?: ActionsConfig['alignment'] },
): ActionsConfig {
  return {
    submit: {
      label: isEdit ? `Update ${entityLabel}` : `Create ${entityLabel}`,
      variant: 'primary',
    },
    cancel: {
      label: 'Cancel',
      variant: 'secondary',
      visible: true,
    },
    reset: { visible: false },
    ...(options?.alignment === undefined ? {} : { alignment: options.alignment }),
  };
}

/**
 * Returns the standard 2-column layout used across all admin entity forms.
 */
export function createFormLayout(): LayoutConfig {
  return {
    columns: 2,
    gap: 'md',
  };
}

/**
 * Creates the standard workflow configuration for admin entity forms.
 *
 * @param cancelRedirectTo - Optional redirect path for the cancel action
 */
export function createFormWorkflow(cancelRedirectTo?: string): WorkflowConfig {
  return {
    onError: {
      mapFieldErrors: true,
    },
    ...(cancelRedirectTo === undefined
      ? {}
      : {
          onCancel: {
            redirect: {
              to: cancelRedirectTo,
            },
          },
        }),
  };
}

/**
 * Creates the standard endpoint configuration for create/edit forms.
 *
 * Handles the common pattern where:
 * - Create mode: POST to `collectionPath`
 * - Edit mode: PUT to `itemPath`, with fetchEndpoint and responseMapping
 *
 * @param isEdit - Whether the form is in edit mode
 * @param collectionPath - The API collection path (e.g. "/api/admin/tenants")
 * @param itemPath - The API item path (e.g. "/api/admin/tenants/123")
 * @param hasIdentifier - Whether an entity identifier was provided (controls fetchEndpoint)
 * @param responseMappingFields - Array of field names to map from `data.*` in the response
 */
export function createFormEndpoint(
  isEdit: boolean,
  collectionPath: string,
  itemPath: string,
  hasIdentifier: boolean,
  responseMappingFields: string[],
): EndpointConfig {
  return {
    erpEndpoint: isEdit ? itemPath : collectionPath,
    method: isEdit ? 'PUT' : 'POST',
    ...(isEdit && hasIdentifier
      ? {
          fetchEndpoint: {
            erpEndpoint: itemPath,
            method: 'GET' as const,
          },
          responseMapping: Object.fromEntries(
            responseMappingFields.map((field) => [field, `data.${field}`]),
          ),
        }
      : {}),
  };
}

/**
 * Creates the standard i18n configuration for create/edit forms.
 *
 * @param entityLabel - The human-readable entity name (e.g. "Tenant", "User")
 * @param isEdit - Whether the form is in edit mode
 */
export function createFormI18n(
  entityLabel: string,
  isEdit: boolean,
): EntityFormI18nOverrides {
  const lowerLabel = entityLabel.toLowerCase();
  return {
    title: isEdit ? `Edit ${entityLabel}` : `Create ${entityLabel}`,
    description: isEdit
      ? `Update the ${lowerLabel} information below.`
      : `Fill in the details to create a new ${lowerLabel}.`,
  };
}

/**
 * Generates a form ID for create mode.
 *
 * @param prefix - Entity prefix (e.g. "tenant", "erp-endpoint")
 */
export function createFormCreateId(prefix: string): string {
  return `${prefix}-create`;
}

/**
 * Generates a form ID for edit mode.
 *
 * @param prefix - Entity prefix (e.g. "tenant", "erp-endpoint")
 * @param identifier - The entity identifier string.
 *   The caller controls the fallback value (e.g. pass `slug ?? 'unknown'`).
 */
export function createFormEditId(prefix: string, identifier: string | undefined): string {
  return `${prefix}-edit-${identifier}`;
}
